"use client";

import Thumbs from "@/assets/Thumbs";
import { Button } from "@/components/atoms/Button";
import {
  // [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
  // FireWidgetViewedEvent,
  minimalEdit,
} from "@/customAnalytics/trackingEvents";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { RootState } from "@/store/store";
import Image from "next/image";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";
import { PhoneIconSVG } from "../../assets/favicons";

interface SubscribeData {
  background_color?: string;
  title?: string;
  title_color?: string;
  description?: string;
  description_color?: string;
  placeholder_text?: string;
  button_name?: string;
  button_background_color?: string;
  button_text_color?: string;
  button_icon?: string;
  success_icon?: string;
  success_text?: string;
  success_text_color?: string;
}

const Subscribe1 = ({ data }: { data: any }) => {
  const { data: sectionData } = data;
  const dispatch = useDispatch();
  const ref = useRef<HTMLElement>(null);
  if (!sectionData) return null;
  if (!sectionData || !sectionData?.status) return null;

  const { lead_submitted_successfully } = useSelector(
    (state: RootState) => state.globalslice
  );

  const {
    background_color = "#EF5A6F",
    title = "",
    title_color = "#FFFFFF",
    description = "",
    description_color = "#FFFFFF",
    placeholder_text = "Enter your phone number",
    button_name = "Call Counsellor",
    button_background_color = "#BE2330",
    button_text_color = "#FFFFFF",
    button_icon = "",
    button_icon_alt = "",
    success_icon = "",
    success_icon_alt = "",
    success_text = "",
    success_text_color = "#FFFFFF",
  } = sectionData || {};

  const [step, setStep] = useState(1);
  const isMobile = useIsMobile(800);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [error, setError] = useState("");

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value && !/^\d*$/.test(value)) {
      setError("Only numbers are allowed");
      return;
    }

    // Check length
    if (value.length > 10) {
      setError("Phone number cannot exceed 10 digits");
      return;
    }

    // For Indian mobile numbers, first digit must be 6, 7, 8, or 9
    // Allow empty value or if first digit is valid
    if (value.length > 0 && !/^[6-9]/.test(value)) {
      setError("Mobile number must start with 6, 7, 8, or 9");
      return;
    }

    setError("");
    setPhoneNumber(value);
  };

  const handlePhoneBlur = () => {
    // Track minimal edit on blur
    if (phoneNumber.length > 0) {
      minimalEdit({
        section: "subscribe_section",
        component: "phone_number_input",
        step: "1",
        entity: "phone_number",
        label: "phone_number_edit",
      });
    }
  };

  const handleSubmit = () => {
    if (phoneNumber.length !== 10) {
      setError("Phone number must be exactly 10 digits");
      return;
    }

    // Validate Indian mobile number format (must start with 6, 7, 8, or 9)
    if (!/^[6-9]/.test(phoneNumber)) {
      setError("Mobile number must start with 6, 7, 8, or 9");
      return;
    }

    dispatch(
      handleOpenLeaddrop({
        variant: LEAD_DROP_VARIANTS.REGISTER,
        phoneNumber: phoneNumber,
      })
    );
  };

  const isValid =
    phoneNumber.length === 10 &&
    /^\d+$/.test(phoneNumber) &&
    /^[6-9]/.test(phoneNumber);

  useEffect(() => {
    if (sessionStorage.getItem("isUserVerified") === "true") {
      setStep(2);
    }
  }, []);

  useEffect(() => {
    if (lead_submitted_successfully) {
      setStep(2);
    }
  }, [lead_submitted_successfully]);

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // Track widget viewed
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "subscribe_section");
  //   }
  // }, []);

  return (
    <section
      ref={ref}
      className={cn(
        "w-full flex justify-center items-center",
        "xs:py-[50px]",
        "sm:py-[42px]",
        "md:py-[42px]",
        "lg:py-[62px]"
      )}
      style={{ backgroundColor: background_color }}
    >
      <div
        className={cn(
          "flex flex-col items-center justify-center",
          "xs:gap-6 xs:px-3",
          "sm:gap-6 sm:w-[568px]",
          "md:w-[630px]",
          "lg:w-[716px]",
          "xl:w-[1388px]"
        )}
      >
        {step === 1 ? (
          <>
            <div
              className={cn(
                "text-center",
                "xs:text-heading-4-semibold",
                "sm:text-heading-6-bold",
                "md:text-heading-3-bold",
                "lg:text-new-heading-36-bold",
                "xl:text-heading-2-bold",
                "line-clamp-2 sm:line-clamp-2",
                "w-full"
              )}
              style={{ color: title_color }}
            >
              {title}
            </div>
            <div
              className={cn(
                "flex flex-col items-center justify-center gap-[10px]",
                "xs:w-full xs:gap-3"
              )}
            >
              {isMobile ? (
                <div
                  className={cn(
                    "xs:p-[10px] bg-white xs:rounded-[10px] xs:w-full xs:flex xs:flex-col xs:gap-[18px]"
                  )}
                >
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    onBlur={handlePhoneBlur}
                    maxLength={10}
                    className={cn(
                      "xs:text-heading-9-semibold text-black text-center xs:p-[14px] bg-gray-1 w-full xs:rounded-[10px]",
                      "placeholder:xs:text-heading-9-semibold placeholder:text-gray-500",
                      "focus:outline-none focus:ring-2 focus:ring-primary-main",
                      error && "ring-2 ring-red-500"
                    )}
                    placeholder={placeholder_text}
                  />

                  <Button
                    onClick={handleSubmit}
                    disabled={!isValid}
                    className={cn(
                      "text-heading-9-semibold",
                      "xs:flex xs:items-center xs:justify-center xs:gap-2 xs:w-full xs:h-11",
                      !isValid && "opacity-50 cursor-not-allowed"
                    )}
                    style={{
                      backgroundColor: button_background_color,
                      color: button_text_color,
                    }}
                  >
                    {button_icon ? (
                      <Image
                        src={button_icon}
                        alt={button_icon_alt}
                        width={20}
                        height={20}
                        className="w-5 h-5"
                      />
                    ) : (
                      <PhoneIconSVG />
                    )}
                    {button_name}
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col gap-1">
                  <div className="relative inline-block">
                    <input
                      id="phone-number"
                      type="tel"
                      value={phoneNumber}
                      onChange={handlePhoneChange}
                      onBlur={handlePhoneBlur}
                      maxLength={10}
                      className={cn(
                        "bg-white text-black",
                        "sm:w-[440px] sm:h-[52px] sm:px-3 sm:py-1 sm:rounded-lg sm:text-heading-9-semibold",
                        "md:w-[468px] md:h-[52px] ",
                        "lg:w-[498px]",
                        "xl:h-[72px] xl:w-[784px]",
                        "placeholder:lg:text-heading-7-semibold",
                        "placeholder:xl:text-heading-6-bold placeholder:text-gray-500",
                        "focus:outline-none focus:ring-2 focus:ring-primary-main",
                        error && "ring-2 ring-red-500"
                      )}
                      placeholder={placeholder_text}
                    />
                    <Button
                      onClick={handleSubmit}
                      disabled={!isValid}
                      className={cn(
                        "flex gap-2 absolute right-3 top-1/2 -translate-y-1/2",
                        !isValid && "opacity-50 cursor-not-allowed",
                        "sm:py-3 sm:px-[18px] sm:text-heading-9-semibold",
                        "xl:text-heading-6-semibold"
                      )}
                      style={{
                        backgroundColor: button_background_color,
                        color: button_text_color,
                      }}
                    >
                      {button_icon ? (
                        <Image
                          src={button_icon}
                          alt={button_icon_alt}
                          width={20}
                          height={20}
                          className="w-5 h-5"
                        />
                      ) : (
                        <PhoneIconSVG />
                      )}
                      {button_name}
                    </Button>
                  </div>
                </div>
              )}

              {description && (
                <div
                  className={cn(
                    "xs:text-heading-9-regular xs:text-center ",
                    "sm:text-heading-9-regular",
                    "lg:text-heading-8-regular",
                    "xl:text-heading-6-regular",
                    "line-clamp-2"
                  )}
                  style={{ color: description_color }}
                >
                  {description}
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            {success_icon ? (
              <Image
                src={success_icon}
                alt={success_icon_alt}
                width={84}
                height={84}
                className={cn(
                  "xs:size-[62px]",
                  "sm:size-[62px]",
                  "lg:size-[74px]",
                  "xl:size-[84px]"
                )}
              />
            ) : (
              <Thumbs
                className={cn(
                  "xs:size-[62px]",
                  "sm:size-[62px]",
                  "lg:size-[74px]",
                  "xl:size-[84px]"
                )}
              />
            )}
            <div
              className={cn(
                "text-center",
                "xs:text-heading-4-semibold xs:px-2",
                "sm:text-heading-6-bold",
                "md:text-heading-3-bold",
                "lg:text-new-heading-36-bold",
                "xl:text-heading-2-bold",
                "line-clamp-3"
              )}
              style={{ color: success_text_color }}
            >
              {success_text}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default Subscribe1;
